/**
 * Lazy loader for xlsx-js-style library
 * This module handles dynamic importing of the xlsx-js-style library
 * to ensure it's only loaded when actually needed at runtime
 */

let xlsxInstance: any = null;
let xlsxLoadingPromise: Promise<any> | null = null;

/**
 * Lazily load and cache the xlsx-js-style library
 * @returns Promise that resolves to the XLSX module
 */
export const getXLSX = async (): Promise<any> => {
	// If already loaded, return cached instance
	if (xlsxInstance) {
		return xlsxInstance;
	}

	// If currently loading, return the existing promise
	if (xlsxLoadingPromise) {
		return xlsxLoadingPromise;
	}

	// Start loading the library
	xlsxLoadingPromise = import('xlsx-js-style').then((module) => {
		xlsxInstance = module;
		xlsxLoadingPromise = null;
		return xlsxInstance;
	});

	return xlsxLoadingPromise;
};

/**
 * Get cached XLSX instance synchronously (only works after initial async load)
 * @returns XLSX module or null if not yet loaded
 */
export const getXLSXSync = (): any => {
	return xlsxInstance;
};

/**
 * Utility function to encode cell reference
 * Lazy loads XLSX when needed
 * (Not used)
 */
// export const lazyEncode_cell = async (cellObj: any): Promise<string> => {
// 	const XLSX = await getXLSX();
// 	return XLSX.utils.encode_cell(cellObj);
// };

/**
 * Utility function to decode range
 * Lazy loads XLSX when needed
 * (Not used)
 */
// export const lazyDecode_range = async (rangeStr: string): Promise<any> => {
// 	const XLSX = await getXLSX();
// 	return XLSX.utils.decode_range(rangeStr);
// };

/**
 * Utility function to encode range
 * Lazy loads XLSX when needed
 * (Not used)
 */
// export const lazyEncode_range = async (s: any, e: any): Promise<string> => {
// 	const XLSX = await getXLSX();
// 	return XLSX.utils.encode_range(s, e);
// };

/**
 * Utility function to convert JSON to sheet
 * Lazy loads XLSX when needed
 */
export const lazyJson_to_sheet = async (data: any): Promise<any> => {
	const XLSX = await getXLSX();
	return XLSX.utils.json_to_sheet(data);
};

/**
 * Utility function to convert array of arrays to sheet
 * Lazy loads XLSX when needed
 */
export const lazyAoa_to_sheet = async (data: any): Promise<any> => {
	const XLSX = await getXLSX();
	return XLSX.utils.aoa_to_sheet(data);
};

/**
 * Utility function to create a new workbook
 * Lazy loads XLSX when needed
 */
export const lazyBook_new = async (): Promise<any> => {
	const XLSX = await getXLSX();
	return XLSX.utils.book_new();
};

/**
 * Utility function to append sheet to workbook
 * Lazy loads XLSX when needed
 */
export const lazyBook_append_sheet = async (wb: any, ws: any, name?: string): Promise<void> => {
	const XLSX = await getXLSX();
	XLSX.utils.book_append_sheet(wb, ws, name);
};

/**
 * Utility function to write workbook
 * Lazy loads XLSX when needed
 */
export const lazyWrite = async (wb: any, opts: any): Promise<any> => {
	const XLSX = await getXLSX();
	return XLSX.write(wb, opts);
};

/**
 * Utility function to write file
 * Lazy loads XLSX when needed
 */
export const lazyWriteFile = async (wb: any, filename: string): Promise<void> => {
	const XLSX = await getXLSX();
	XLSX.writeFile(wb, filename);
};

